import React from 'react';
import { useGeo } from '../contexts/GeoContext';

const VAT_RATE = 0.20;

/**
 * Parse "£600", "£10,000", "From £400", "£5,000 portfolio of 4" → numeric, plus suffix text.
 * Returns { amount: number|null, prefix: string, suffix: string, raw: string, isPOR: boolean }.
 */
const parsePrice = (priceDisplay) => {
  const raw = (priceDisplay || '').trim();
  if (!raw) return { amount: null, prefix: '', suffix: '', raw, isPOR: true };
  if (/price on request/i.test(raw)) return { amount: null, prefix: '', suffix: '', raw, isPOR: true };
  const match = raw.match(/^(.*?)£([\d,]+(?:\.\d+)?)(.*)$/);
  if (!match) return { amount: null, prefix: '', suffix: '', raw, isPOR: false };
  const [, prefix, num, suffix] = match;
  const amount = parseFloat(num.replace(/,/g, ''));
  return { amount, prefix: prefix.trim(), suffix: suffix.trim(), raw, isPOR: false };
};

const formatGBP = (n) => {
  if (n >= 1000) return `£${Math.round(n).toLocaleString('en-GB')}`;
  return `£${Math.round(n)}`;
};

/**
 * Renders an artwork price with geo + VAT scheme awareness.
 *
 *   <PriceTag priceDisplay="£600" vatScheme="standard" />
 *
 *   UK visitor, standard scheme : "£720"        (incl. VAT)
 *   non-UK,      standard scheme: "£600"        (excl. UK VAT)
 *   any visitor, margin scheme  : "£10,000"     (VAT Margin Scheme — no further VAT)
 *   Price on Request            : "Price on Request"
 *
 * Variants:
 *   `compact` — used in shop card grids (single line, smaller note)
 *   `full`    — used on artwork detail page (larger price + explainer)
 */
const PriceTag = ({ priceDisplay, vatScheme = 'standard', variant = 'compact', className = '' }) => {
  const { isUK, loading } = useGeo();
  const parsed = parsePrice(priceDisplay);

  if (parsed.isPOR) {
    return <span className={className} data-testid="price-tag-por">Enquire</span>;
  }

  // Could not parse — render verbatim (no VAT logic applied)
  if (parsed.amount === null) {
    return <span className={className} data-testid="price-tag-raw">{parsed.raw}</span>;
  }

  let displayAmount = parsed.amount;
  let note = '';

  // House policy: display the price EXACTLY as set by the gallery — no VAT
  // gross-up, no advisory notes. Any VAT position is handled at enquiry /
  // invoice time, not on the marketing surface.

  const main = `${parsed.prefix ? parsed.prefix + ' ' : ''}${formatGBP(displayAmount)}${parsed.suffix ? ' ' + parsed.suffix : ''}`;

  if (variant === 'full') {
    return (
      <div className={className} data-testid="price-tag-full">
        <div className="text-2xl sm:text-3xl font-light tracking-tight">{main}</div>
        {note && <div className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500">{note}</div>}
      </div>
    );
  }
  // Compact (grid) variant — price only. VAT / margin-scheme notes are
  // reserved for the PDP and the enquiry modal so listing cards stay clean.
  return (
    <span className={className} data-testid="price-tag-compact">
      <span className="font-medium text-black">{main}</span>
    </span>
  );
};

export default PriceTag;
